import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  TrendingUp, DollarSign, FileSpreadsheet, Calculator, CheckCircle,
  ArrowRight, Info,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import axios from "axios";
import { toast } from "sonner";
import { formatDollars } from "@/lib/language";
import { fadeInUp, staggerContainer, staggerItem, hoverLift } from "@/config/animations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SavingsReport() {
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [verifyData, setVerifyData] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [projMonths, setProjMonths] = useState(12);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/savings/summary`),
      axios.get(`${API}/savings/history?limit=50`),
      axios.get(`${API}/invoices`),
    ]).then(([s, h, inv]) => {
      setSummary(s.data);
      setHistory(h.data);
      setInvoices(inv.data);
    }).catch(() => {});
  }, []);

  const handleVerify = async (optId) => {
    try {
      const r = await axios.get(`${API}/savings/verify/${optId}`);
      setVerifyData(r.data);
      setVerifyOpen(true);
    } catch { toast.error("Could not load verification"); }
  };

  const handleGenerateInvoice = async () => {
    try {
      const r = await axios.post(`${API}/invoices/generate`, {});
      toast.success("Invoice generated!");
      setInvoices(prev => [r.data, ...prev]);
    } catch { toast.error("Failed to generate invoice"); }
  };

  const handleExportCSV = () => {
    if (!history.length) return;
    const headers = "Date,Workflow,Baseline,Optimized,Saved,Pct,Fee,Net\n";
    const rows = history.map(h =>
      `${h.created_at},${h.workflow_id},${h.baseline_cost},${h.optimized_cost},${h.dollars_saved},${h.savings_pct}%,${h.qnneal_fee},${h.net_savings}`
    ).join("\n");
    const blob = new Blob([headers + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "qnneal-savings-report.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported!");
  };

  // Chart data: group by date
  const chartData = history.slice(0, 20).reverse().map((h, i) => ({
    name: h.created_at ? new Date(h.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : `#${i + 1}`,
    savings: h.dollars_saved,
    fee: h.qnneal_fee,
  }));

  // ROI projection
  const monthlySaved = summary?.month?.saved || 0;
  const monthlyFee = summary?.month?.fee || 0;
  const projectedSavings = monthlySaved * projMonths;
  const projectedFees = monthlyFee * projMonths;
  const projectedNet = projectedSavings - projectedFees;

  return (
    <motion.div {...fadeInUp} className="space-y-8" data-testid="savings-report" data-automation-id="savings-report">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--qn-foreground)]">Savings Report</h2>
          <p className="text-sm text-[var(--qn-foreground-muted)]">Track your optimization savings, fees, and ROI</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="export-savings-csv" data-automation-id="export-savings-csv">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />CSV
          </Button>
          <Button size="sm" className="btn-primary" onClick={handleGenerateInvoice} data-testid="generate-invoice-btn" data-automation-id="generate-invoice-btn">
            <DollarSign className="w-3.5 h-3.5 mr-1.5" />Generate Invoice
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Total Saved", value: `$${summary.total_saved.toFixed(2)}`, color: "var(--qn-success)" },
            { label: "QNNEAL Fee", value: `$${summary.total_fee.toFixed(2)}`, color: "var(--qn-warning)" },
            { label: "Your Net Savings", value: `$${summary.net_savings.toFixed(2)}`, color: "var(--qn-success)" },
            { label: "ROI", value: summary.total_fee > 0 ? `${Math.round((summary.net_savings / summary.total_fee) * 100)}%` : "N/A", color: "var(--qn-primary)" },
          ].map((c, i) => (
            <motion.div key={i} variants={staggerItem} whileHover={hoverLift.whileHover}>
              <Card className="card-themed">
                <CardContent className="p-5">
                  <div className="text-xs text-[var(--qn-foreground-muted)] mb-1">{c.label}</div>
                  <div className="text-2xl font-mono font-bold" style={{ color: c.color }}>{c.value}</div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Savings Chart */}
      {chartData.length > 0 && (
        <Card className="card-themed">
          <CardHeader>
            <CardTitle className="text-base">Savings per Optimization</CardTitle>
          </CardHeader>
          <CardContent className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--qn-chart-grid)" />
                <XAxis dataKey="name" stroke="var(--qn-foreground-subtle)" fontSize={11} />
                <YAxis stroke="var(--qn-foreground-subtle)" fontSize={11} />
                <Tooltip
                  contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: "8px", color: "var(--qn-foreground)" }}
                  wrapperStyle={{ outline: "none" }}
                  cursor={{ fill: "rgba(255,255,255,0.06)" }}
                  formatter={(val) => [`$${val.toFixed(4)}`, ""]}
                />
                <Legend />
                <Bar dataKey="savings" name="Savings" fill="var(--qn-success)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="fee" name="QNNEAL Fee" fill="var(--qn-warning)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* ROI Calculator */}
      <Card className="card-themed border-[var(--qn-primary)]/20">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="w-4 h-4 text-[var(--qn-primary)]" />
            ROI Projection
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <span className="text-sm text-[var(--qn-foreground-muted)]">Project over</span>
            {[3, 6, 12, 24].map(m => (
              <Button
                key={m}
                variant={projMonths === m ? "default" : "outline"}
                size="sm"
                onClick={() => setProjMonths(m)}
                className={projMonths === m ? "btn-primary" : ""}
                data-testid={`roi-${m}mo`}
              >
                {m} months
              </Button>
            ))}
          </div>
          <div className="bg-[var(--qn-surface)] rounded-lg p-5 text-center">
            <p className="text-sm text-[var(--qn-foreground-muted)] mb-2">At your current rate, over {projMonths} months:</p>
            <div className="text-3xl font-mono font-bold text-[var(--qn-success)]" data-testid="roi-projected-savings" data-automation-id="roi-projected-savings">
              ${projectedNet.toFixed(2)}
            </div>
            <p className="text-sm text-[var(--qn-foreground-muted)] mt-1">
              in net savings (${projectedSavings.toFixed(2)} total - ${projectedFees.toFixed(2)} fees)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Savings Breakdown Table */}
      {history.length > 0 && (
        <Card className="card-themed">
          <CardHeader>
            <CardTitle className="text-base">Savings Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--qn-border)]">
                    <th className="text-left py-2 px-3 text-[var(--qn-foreground-subtle)]">Date</th>
                    <th className="text-left py-2 px-3 text-[var(--qn-foreground-subtle)]">Workflow</th>
                    <th className="text-right py-2 px-3 text-[var(--qn-foreground-subtle)]">Baseline</th>
                    <th className="text-right py-2 px-3 text-[var(--qn-foreground-subtle)]">Optimized</th>
                    <th className="text-right py-2 px-3 text-[var(--qn-foreground-subtle)]">Saved</th>
                    <th className="text-right py-2 px-3 text-[var(--qn-foreground-subtle)]">Fee</th>
                    <th className="text-right py-2 px-3 text-[var(--qn-foreground-subtle)]">Net</th>
                    <th className="text-center py-2 px-3 text-[var(--qn-foreground-subtle)]">Verify</th>
                  </tr>
                </thead>
                <tbody>
                  {history.slice(0, 20).map((h, i) => (
                    <tr key={i} className="border-b border-[var(--qn-border)]/50 hover:bg-[var(--qn-surface)]">
                      <td className="py-2 px-3 text-xs">{h.created_at ? new Date(h.created_at).toLocaleDateString() : ""}</td>
                      <td className="py-2 px-3 font-mono text-xs">{h.workflow_id}</td>
                      <td className="py-2 px-3 text-right font-mono">${h.baseline_cost.toFixed(4)}</td>
                      <td className="py-2 px-3 text-right font-mono">${h.optimized_cost.toFixed(4)}</td>
                      <td className="py-2 px-3 text-right font-mono text-[var(--qn-success)]">${h.dollars_saved.toFixed(4)}</td>
                      <td className="py-2 px-3 text-right font-mono text-[var(--qn-warning)]">${h.qnneal_fee.toFixed(4)}</td>
                      <td className="py-2 px-3 text-right font-mono font-bold">${h.net_savings.toFixed(4)}</td>
                      <td className="py-2 px-3 text-center">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleVerify(h.optimization_id)} data-testid={`verify-${i}`}>
                          <Info className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoices */}
      {invoices.length > 0 && (
        <Card className="card-themed">
          <CardHeader><CardTitle className="text-base">Invoices</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {invoices.map((inv, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[var(--qn-surface)]" data-testid={`invoice-${i}`}>
                  <div>
                    <div className="text-sm font-medium">{inv.billing_period_start ? new Date(inv.billing_period_start).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "Current"}</div>
                    <div className="text-xs text-[var(--qn-foreground-subtle)]">{inv.total_optimizations} optimizations</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-sm font-mono font-bold">${inv.total_charge?.toFixed(2)}</div>
                      <Badge variant="outline" className="text-xs">{inv.payment_status}</Badge>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => window.open(`${API}/invoices/${inv.invoice_id}/pdf`, '_blank')} data-testid={`invoice-pdf-${i}`}>
                      PDF
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Savings Verification Dialog */}
      <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)] max-w-lg">
          {verifyData && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[var(--qn-success)]" />
                  How we calculated your savings
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {verifyData.steps.map((s, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <div className="w-6 h-6 rounded-full bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] flex items-center justify-center text-xs font-bold shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <div className="text-sm">{s.description}</div>
                      <div className="text-sm font-mono text-[var(--qn-foreground-muted)]">{s.detail}</div>
                    </div>
                  </div>
                ))}
                <div className="mt-4 p-3 rounded-lg bg-[var(--qn-success-muted)] border border-[var(--qn-success)]/20">
                  <div className="text-xs text-[var(--qn-foreground-subtle)] mb-1">All calculations use real-time model pricing from OpenAI, Anthropic, and Google.</div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
